import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
    Car, Calendar, Users, Shield, CheckCircle2,
    PhoneIcon, MailIcon, Smartphone, UserCheck, MessageSquare, ClipboardList,
    GraduationCap, Star, Quote, Award, Download,
    ArrowRight,
} from 'lucide-react';
import { Spin } from 'antd';
import { GlobalOutlined, TeamOutlined } from '@ant-design/icons';
import { ChatIcon, EmailIcon, OfficeIcon } from './svg/icons';
import { useDispatch, useSelector } from 'react-redux';
import { commonActions } from '../actions/commonActions';
import { reviewActions } from '../actions/reviewActions';
import { getblobLink } from '../services/helper';
import { useNavigate } from 'react-router-dom';
import { APPSTORE_URL, PLAYSTORE_URL } from '../services/env';

const QRCodeCanvas = React.lazy(() =>
    import('qrcode.react').then(m => ({ default: m.QRCodeCanvas }))
);

// InfoItemProps for Contact section
interface InfoItemProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    linkLabel: string;
    href: string;
    delay: string;
    multiLine?: boolean;
    onClick?: () => void;
}

// ============================================
// STATS STRIP
// ============================================
const StatsStrip = () => {
    const stats = [
        { value: '7', label: 'Days Free Trial' },
        { value: '93%', label: 'Pass Rate' },
        { value: '10K+', label: 'Students Enrolled' },
    ];

    return (
        <section className="py-12 bg-white border-y border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-3 gap-8">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="text-center"
                        >
                            <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--primary-color)] mb-2">{stat.value}</p>
                            <p className="text-sm sm:text-base text-slate-500 font-medium uppercase tracking-wide">{stat.label}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ============================================
// WHAT IS IT SECTION (Roadmap Journey Style)
// ============================================
const WhatIsIt = () => {
    const steps = [
        {
            number: "01",
            title: "Register & Start Training!",
            description: "Once your registration is confirmed, you get immediate access via email to online training and the mobile app. Enjoy a 7-day free trial period (just ₹499/month, cancel anytime).",
            align: "left",
            topPercent: 0
        },
        {
            number: "02",
            title: "Ask Your Questions",
            description: "Facing difficulties or don't understand a question? Our team accompanies you and answers all your questions throughout your driving training.",
            align: "right",
            topPercent: 18
        },
        {
            number: "03",
            title: "Progress Step by Step",
            description: "At your own pace, learn and review the driving rules. Track your progress and see where your gaps are. Our algorithm estimates if you're ready for the final exam or not!",
            align: "left",
            topPercent: 38
        },
        {
            number: "04",
            title: "Book Your Slot for the Exam",
            description: "Reserve your place for the driving test without leaving your home! Register directly from the web interface or via the mobile app.",
            align: "right",
            topPercent: 58
        },
        {
            number: "05",
            title: "Get Your License",
            description: "Go to the exam on the date you reserved, to complete your training and get your driving license with confidence!",
            align: "left",
            topPercent: 78
        },
    ];

    return (
        <section id="what-is-it" className="py-16 bg-white scroll-mt-16 overflow-hidden">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--primary-color)]" style={{ fontStyle: 'italic' }}>
                            How to get your
                            <br />
                            driving license?
                        </h2>
                    </motion.div>
                </div>

                {/* Roadmap Journey - Image driven layout */}
                <div className="relative">
                    {/* Center road image container */}
                    <div className="flex justify-center">
                        <div className="w-[280px] sm:w-[320px] md:w-[380px] lg:w-[420px]">
                            <img
                                src={`${getblobLink()}/public/asset/drivingBackground.webp`}
                                alt="Road journey"
                                className="w-full h-auto"
                                loading="lazy"
                                width="420"
                                height="800"
                            />
                        </div>
                    </div>

                    {/* Left side content */}
                    <div className="absolute left-0 top-0 bottom-0 w-[38%] hidden md:block">
                        {steps.filter(s => s.align === 'left').map((step) => (
                            <motion.div
                                key={step.number}
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5 }}
                                className="absolute right-0 pr-4 max-w-xs lg:max-w-sm"
                                style={{ top: `${step.topPercent}%` }}
                            >
                                <span className="inline-block px-2.5 py-0.5 bg-[var(--primary-color)] text-white rounded text-[10px] font-semibold mb-2 uppercase tracking-wider">
                                    Step {step.number}
                                </span>
                                <h3 className="text-lg lg:text-xl font-bold text-slate-800 mb-2" style={{ fontStyle: 'italic' }}>
                                    {step.title}
                                </h3>
                                <p className="text-slate-500 text-xs lg:text-sm leading-relaxed">
                                    {step.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                    <div className="absolute right-0 top-0 bottom-0 w-[38%] hidden md:block">
                        {steps.filter(s => s.align === 'right').map((step) => (
                            <motion.div
                                key={step.number} initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5 }}
                                className="absolute left-0 pl-4 max-w-xs lg:max-w-sm text-right"
                                style={{ top: `${step.topPercent}%` }}
                            >
                                <span className="inline-block px-2.5 py-0.5 bg-[var(--primary-color)] text-white rounded text-[10px] font-semibold mb-2 uppercase tracking-wider">
                                    Step {step.number}
                                </span>
                                <h3 className="text-lg lg:text-xl font-bold text-slate-800 mb-2" style={{ fontStyle: 'italic' }}>
                                    {step.title}
                                </h3>
                                <p className="text-slate-500 text-xs lg:text-sm leading-relaxed">
                                    {step.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
                <div className="md:hidden space-y-8 mt-8">
                    {steps.map((step) => (
                        <motion.div
                            key={step.number}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4 }}
                            className="text-center"
                        >
                            <span className="inline-block px-2.5 py-0.5 bg-[var(--primary-color)] text-white rounded text-[10px] font-semibold mb-2 uppercase tracking-wider">
                                Step {step.number}
                            </span>
                            <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontStyle: 'italic' }}>
                                {step.title}
                            </h3>
                            <p className="text-slate-500 text-sm leading-relaxed max-w-sm mx-auto">
                                {step.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ============================================
// HOW IT WORKS SECTION
// ============================================
const HowItWorks = () => {
    const steps = [
        {
            number: "01",
            title: "Register Your School",
            description: "Sign up and set up your driving school profile in minutes. Add your services, vehicles, and instructors.",
            icon: <GraduationCap className="w-6 h-6" />
        },
        {
            number: "02",
            title: "Enroll Students",
            description: "Students can register online and enroll in courses. Manage documents and payments seamlessly.",
            icon: <Users className="w-6 h-6" />
        },
        {
            number: "03",
            title: "Schedule & Track",
            description: "Create batches, assign instructors, and let students track their progress through the mobile app.",
            icon: <Calendar className="w-6 h-6" />
        },
        {
            number: "04",
            title: "Success!",
            description: "Students complete their training and pass their driving test with confidence. Track pass rates and grow your school.",
            icon: <Award className="w-6 h-6" />
        },
    ];

    return (
        <section id="how-it-works" className="py-20 bg-slate-50 scroll-mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section header */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="inline-block px-4 py-1.5 bg-emerald-50 text-emerald-600 rounded-full text-sm font-semibold">
                            How it Works
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">
                            Get Started in
                            <span className="text-[var(--primary-color)]"> 4 Simple Steps</span>
                        </h2>
                    </motion.div>
                </div>

                {/* Steps */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {steps.map((step, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.15 }}
                            className="relative"
                        >
                            {/* Connector line */}
                            {index < steps.length - 1 && (
                                <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-slate-200 -translate-x-1/2" />
                            )}

                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 relative h-full">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 bg-[var(--primary-color)] text-white rounded-xl flex items-center justify-center font-bold">
                                        {step.number}
                                    </div>
                                    <div className="w-10 h-10 bg-[var(--primary-color-lighter,#EEF2FF)] text-[var(--primary-color)] rounded-lg flex items-center justify-center">
                                        {step.icon}
                                    </div>
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                                <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ============================================
// TESTIMONIALS SECTION
// ============================================
const Testimonials = () => {
    const testimonials = [
        {
            name: "Priya Sharma",
            role: "Driving School Owner",
            content: "DriveMynt transformed how we manage our school. The batch scheduling and attendance tracking features saved us hours every week. Highly recommended!",
            rating: 5,
            avatar: "PS"
        },
        {
            name: "Rahul Kumar",
            role: "Student",
            content: "I passed my driving test on the first attempt! The progress tracking helped me understand where I needed more practice. The mobile app made scheduling lessons so easy.",
            rating: 5,
            avatar: "RK"
        },
        {
            name: "Amit Patel",
            role: "Instructor",
            content: "The instructor app is fantastic. I can see my schedule, mark attendance, and communicate with students all in one place. It's made my job so much easier.",
            rating: 5,
            avatar: "AP"
        },
    ];

    const [currentIndex, setCurrentIndex] = useState(0);

    return (
        <section id="testimonials" className="py-20 bg-white scroll-mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section header */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="inline-block px-4 py-1.5 bg-amber-50 text-amber-600 rounded-full text-sm font-semibold mb-4">
                            Testimonials
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                            Loved by Schools &
                            <span className="text-[var(--primary-color)]"> Students</span>
                        </h2>
                    </motion.div>
                </div>

                {/* Testimonials carousel */}
                <div className="relative">
                    <div className="grid md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-slate-50 p-6 rounded-2xl relative"
                            >
                                {/* Quote icon */}
                                <div className="absolute -top-3 -left-3 w-8 h-8 bg-[var(--primary-color)] rounded-lg flex items-center justify-center">
                                    <Quote className="w-4 h-4 text-white" />
                                </div>

                                {/* Stars */}
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                                    ))}
                                </div>

                                {/* Content */}
                                <p className="text-slate-600 mb-6 leading-relaxed">"{testimonial.content}"</p>

                                {/* Author */}
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[var(--primary-color-light,#E0E7FF)] text-[var(--primary-color)] rounded-full flex items-center justify-center font-semibold text-sm">
                                        {testimonial.avatar}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-900">{testimonial.name}</p>
                                        <p className="text-sm text-slate-500">{testimonial.role}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ============================================
// MOBILE APP SECTION
// ============================================
// Replace with iOS App Store link when live

const MobileApp = () => {
    const instructorFeatures = [
        { icon: <ClipboardList className="w-5 h-5" />, title: "View Batches", description: "See assigned batches and student rosters" },
        { icon: <UserCheck className="w-5 h-5" />, title: "Mark Attendance", description: "Quick attendance marking in real-time" },
        { icon: <MessageSquare className="w-5 h-5" />, title: "Raise Queries", description: "Communicate with admin instantly" }
    ];

    const studentFeatures = [
        { icon: <GraduationCap className="w-5 h-5" />, title: "Track Progress", description: "Monitor your learning journey" },
        { icon: <Calendar className="w-5 h-5" />, title: "View Schedule", description: "Check upcoming lessons and batches" },
        { icon: <MessageSquare className="w-5 h-5" />, title: "Get Support", description: "Submit queries and get help" }
    ];

    return (
        <section id="mobile-app" className="py-20 bg-slate-900 text-white scroll-mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section header */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 text-white rounded-full text-sm font-semibold">
                            <Smartphone className="w-4 h-4" />
                            Mobile App
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                            Learn & Manage
                            <span className="text-[var(--primary-color-mid,#818CF8)]"> Anywhere</span>
                        </h2>
                        <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                            Download our mobile apps for iOS and Android. Stay connected whether you're teaching or learning.
                        </p>
                    </motion.div>
                </div>

                {/* Two columns */}
                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Instructor app */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="bg-white/5 backdrop-blur-sm p-8 rounded-3xl border border-white/10"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary-color)] text-white rounded-full text-sm font-semibold mb-6">
                            <Users className="w-4 h-4" />
                            For Instructors
                        </div>
                        <h3 className="text-2xl font-bold">Empower Your Teaching</h3>
                        <p className="text-slate-300 mb-8">Everything instructors need to manage classes effectively.</p>
                        <div className="space-y-4">
                            {instructorFeatures.map((feature, index) => (
                                <div key={index} className="flex gap-4 items-start">
                                    <div className="w-10 h-10 bg-[var(--primary-color)]/20 text-[var(--primary-color-mid,#818CF8)] rounded-lg flex items-center justify-center flex-shrink-0">
                                        {feature.icon}
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white">{feature.title}</h4>
                                        <p className="text-sm text-slate-400">{feature.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Student app */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className="bg-white/5 backdrop-blur-sm p-8 rounded-3xl border border-white/10"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-semibold mb-6">
                            <GraduationCap className="w-4 h-4" />
                            For Students
                        </div>
                        <h3 className="text-2xl font-bold">Track Your Journey</h3>
                        <p className="text-slate-300 mb-8">Stay informed about your progress and upcoming lessons.</p>
                        <div className="space-y-4">
                            {studentFeatures.map((feature, index) => (
                                <div key={index} className="flex gap-4 items-start">
                                    <div className="w-10 h-10 bg-emerald-600/20 text-emerald-400 rounded-lg flex items-center justify-center flex-shrink-0">
                                        {feature.icon}
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white">{feature.title}</h4>
                                        <p className="text-sm text-slate-400">{feature.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Download CTA */}
                <div className="mt-16">
                    <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-12 text-center">
                        <p className="text-white text-lg font-semibold mb-2">Available on iOS and Android</p>
                        <p className="text-slate-400 text-sm max-w-xl mx-auto mb-18 leading-relaxed">
                            Get started in seconds! Simply scan the QR code with your phone camera or click the store badge below to install the app directly on your device.
                        </p>

                        <div className="flex flex-wrap justify-center items-center gap-12 sm:gap-16">
                            {/* Android QR & Action */}
                            <div className="flex flex-col items-center gap-4">
                                <div className="bg-white p-4 rounded-2xl shadow-lg">
                                    <React.Suspense fallback={<div style={{ width: 150, height: 150 }} />}>
                                        <QRCodeCanvas
                                            value={PLAYSTORE_URL}
                                            size={150}
                                            level="M"
                                        />
                                    </React.Suspense>
                                </div>
                                <a
                                    href={PLAYSTORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="transition-transform hover:scale-105"
                                    title="Get it on Google Play"
                                >
                                    <img src="/assets/google-play-badge.svg" alt="Google Play" className="h-11" />
                                </a>
                                <span className="text-slate-400 text-xs">Scan QR or tap to download for Android</span>
                            </div>

                            {/* iOS QR & Action */}
                            <div className="flex flex-col items-center gap-4">
                                <div className="bg-white p-4 rounded-2xl shadow-lg">
                                    <React.Suspense fallback={<div style={{ width: 150, height: 150 }} />}>
                                        <QRCodeCanvas
                                            value={APPSTORE_URL}
                                            size={150}
                                            level="M"
                                        />
                                    </React.Suspense>
                                </div>
                                <a
                                    href={APPSTORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="transition-transform hover:scale-105"
                                    title="Download on the App Store"
                                >
                                    <img src="/assets/app-store-badge.svg" alt="Download on the App Store" className="h-11" />
                                </a>
                                <span className="text-slate-400 text-xs">Scan QR or tap to download for iOS</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// ============================================
// ABOUT SECTION
// ============================================
const About = () => {
    const roles = [
        {
            title: "For Admins",
            icon: <Shield className="w-5 h-5" />,
            benefits: [
                "Complete oversight of operations",
                "User and instructor management",
                "Revenue tracking and reports",
                "Service and expense management"
            ]
        },
        {
            title: "For Instructors",
            icon: <Users className="w-5 h-5" />,
            benefits: [
                "View assigned batches",
                "Mark attendance easily",
                "Access vehicle schedules",
                "Communicate with students"
            ]
        },
        {
            title: "For Students",
            icon: <GraduationCap className="w-5 h-5" />,
            benefits: [
                "Enroll in courses online",
                "Track your progress",
                "View batch schedules",
                "Submit queries and get help"
            ]
        }
    ];

    return (
        <section id="about" className="py-20 bg-slate-50 scroll-mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="inline-block px-4 py-1.5 bg-[var(--primary-color-lighter,#EEF2FF)] text-[var(--primary-color)] rounded-full text-sm font-semibold">
                            About DriveMynt
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                            Built for Modern
                            <span className="text-[var(--primary-color)]"> Driving Schools</span>
                        </h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            DriveMynt was created to transform traditional driving school management into a modern, efficient, and student-centric digital platform.
                        </p>
                    </motion.div>
                </div>

                {/* Role cards */}
                <div className="grid md:grid-cols-3 gap-8 mb-16">
                    {roles.map((role, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-white p-8 rounded-2xl border border-slate-200 hover:shadow-lg transition-shadow"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-[var(--primary-color-light,#E0E7FF)] text-[var(--primary-color)] rounded-xl flex items-center justify-center">
                                    {role.icon}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900">{role.title}</h3>
                            </div>
                            <ul className="space-y-3">
                                {role.benefits.map((benefit, i) => (
                                    <li key={i} className="flex items-start gap-3 text-slate-600">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                        <span>{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {[
                        { value: "100%", label: "Digital" },
                        { value: "Real-Time", label: "Updates" },
                        { value: "Secure", label: "Data" },
                        { value: "24/7", label: "Access" }
                    ].map((stat, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="text-center"
                        >
                            <p className="text-2xl md:text-3xl font-bold text-[var(--primary-color)] mb-1">{stat.value}</p>
                            <p className="text-sm text-slate-500 uppercase tracking-wide">{stat.label}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ============================================
// SCHOOL REVIEWS / DIRECTORY SECTION
// ============================================
const StarRating = ({ rating }: { rating: number | null }) => {
    const r = rating ?? 0;
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    className={`w-4 h-4 ${i <= Math.round(r) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
                />
            ))}
            <span className="text-sm text-slate-500 ml-1">{r > 0 ? r.toFixed(1) : 'No rating'}</span>
        </div>
    );
};

const SchoolCard = ({ school }: { school: any }) => {
    const imgUrl = school.profileImageUrl ? school.profileImageUrl : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6 flex flex-col h-[160px] sm:h-[180px]"
        >
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
                {imgUrl ? (
                    <img src={imgUrl} alt={school.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border border-slate-100 flex-shrink-0" />
                ) : (
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[var(--primary-color-lighter,#EEF2FF)] flex items-center justify-center text-[var(--primary-color)] font-bold text-base sm:text-lg flex-shrink-0">
                        {school.name?.[0]?.toUpperCase() ?? 'S'}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 truncate text-sm sm:text-base">{school.name}</h3>
                    <p className="text-xs text-slate-400 truncate">{school.address || school.country || 'Location not set'}</p>
                </div>
            </div>
            <StarRating rating={school.averageSchoolRating} />
            <p className="text-xs text-slate-400 mt-auto pt-2">{school.totalReviews} review{school.totalReviews !== 1 ? 's' : ''}</p>
        </motion.div>
    );
};

const SchoolReviews = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const schoolData = useSelector((state: any) => state.getSchoolByRatingsReducer);
    const schools: any[] = schoolData?.schooldata?.data ?? [];

    useEffect(() => {
        dispatch(reviewActions.getSchoolByRatings({ page: 1, limit: 6 }));
    }, []);

    return (
        <section id="schools" className="py-20 bg-slate-50 scroll-mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <span className="inline-block px-4 py-1.5 bg-amber-50 text-amber-600 rounded-full text-sm font-semibold mb-4">
                            Trusted Schools
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                            Top Rated
                            <span className="text-[var(--primary-color)]"> Driving Schools</span>
                        </h2>
                        <p className="text-slate-500 text-lg max-w-2xl mx-auto">
                            Discover driving schools trusted by thousands of students.
                        </p>
                    </motion.div>
                </div>

                {schoolData?.isLoading ? (
                    <div className="flex justify-center py-12"><Spin size="large" /></div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {schools.slice(0, 6).map((school: any) => (
                            <SchoolCard key={school.tenantId} school={school} />
                        ))}
                    </div>
                )}

                <div className="text-center mt-10">
                    <button
                        onClick={() => navigate('/schools')}
                        className="inline-flex items-center gap-2 bg-[var(--primary-color)] text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-all shadow-lg"
                    >
                        View All Schools <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </section>
    );
};

// ============================================
// CONTACT SECTION
// ============================================
function InfoItem({ icon, title, description, linkLabel, href, delay, multiLine, onClick }: InfoItemProps) {

    return (
        <div className="flex flex-col gap-2" style={{ animationDelay: delay }}>
            <div className="w-10 h-10 rounded-full bg-[var(--primary-color-lighter,#EEF2FF)] border border-[var(--primary-color-light,#E0E7FF)] flex items-center justify-center text-[var(--primary-color)] mb-1">
                {icon}
            </div>
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <p className="w-[120px] sm:w-auto text-sm! text-slate-500 break-words">{description}</p>
            {onClick ? (
                <button onClick={onClick}
                    className="text-sm! font-semibold cursor-pointer text-[var(--primary-color)]! hover:underline text-left">
                    {linkLabel}
                </button>
            ) : (
                <a href={href} className="text-sm font-semibold text-[var(--primary-color)] hover:underline">
                    {multiLine ? linkLabel.split("\n").map((line, i) => <span key={i} className="block">{line}</span>) : linkLabel}
                </a>
            )}
        </div>
    );
}

const Contact = () => {
    const [form, setForm] = useState({ name: "", phoneNumber: "", email: "", message: "" });
    const [focused, setFocused] = useState<string | null>(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const emaildata = useSelector((state: any) => state.sendemailReducer);

    const handleOpenChatbot = () => {
        window.dispatchEvent(new CustomEvent('open-chatbot'));
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        if (name === 'phoneNumber') {
            const digits = (value || '').toString().replace(/\D/g, '').slice(0, 10);
            setForm((p) => ({ ...p, phoneNumber: digits }));
            return;
        }
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleClickLogin = () => {
        const authData = localStorage.getItem('AUTHDATA');
        if (!authData) {
            navigate('/login');
        } else {
            navigate('/dashboard');
        }
    }

    const handleSubmit = async (e: any) => {
        dispatch(commonActions.sendMail(form));
    };

    const inputBase = (name: string) =>
        `w-full px-4 py-3 rounded-xl text-sm text-slate-900 bg-white border outline-none transition-all ${focused === name ? 'border-[var(--primary-color)] shadow-sm' : 'border-slate-200 hover:border-slate-300'}`;

    return (
        <section id="contact" className="py-20 bg-white scroll-mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-14">
                    <span className="text-sm font-semibold text-[var(--primary-color)] mb-3 block">Contact us</span>
                    <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Get in Touch</h2>
                    <p className="text-slate-600 text-lg">We'd love to hear from you. Send us a message!</p>
                </div>

                <div className="grid lg:grid-cols-2 gap-16">
                    <div className="grid grid-cols-2 gap-10">
                        <InfoItem delay="0ms" icon={<EmailIcon />}
                            title="Email" description="Our team is here to help." linkLabel="contact@auxwit.com" href="mailto:contact@auxwit.com" />
                        <InfoItem delay="100ms" icon={<ChatIcon />}
                            title="FAQ Assistant" description="Get help with your questions" linkLabel="Start chat" href="#" onClick={handleOpenChatbot} />
                        <InfoItem delay="200ms" icon={<TeamOutlined />}
                            title="Meet Our Team"
                            description="Meet our experts for assistance" linkLabel="Get Started" href="#" multiLine onClick={handleClickLogin} />
                        <InfoItem delay="300ms" icon={<PhoneIcon />}
                            title="Phone" description="Mon–Fri 10am–5pm" linkLabel="+91(44) 2278-1279" href="tel:+914422781279" />
                    </div>

                    {/* Contact form */}
                    <div className="bg-slate-50 rounded-2xl p-8 relative">
                        {emaildata?.isLoading && <div className="absolute z-0 inset-0 flex items-center justify-center"><Spin /></div>}
                        {emaildata?.isSuccess ? (
                            <div className="flex flex-col items-center justify-center py-10 text-center">
                                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
                                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-2">Message sent!</h3>
                                <p className="text-slate-500 mb-6">We'll get back to you within 24 hours.</p>
                                <button onClick={() => { emaildata.isSuccess = false; setForm({ name: "", phoneNumber: "", email: "", message: "" }); }} className="text-sm font-semibold text-[var(--primary-color)] hover:underline">
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-slate-700 block mb-2">Name</label>
                                        <input type="text" name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" className={inputBase("name")} required />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-slate-700 block mb-2">Phone</label>
                                        <input type="tel" inputMode="numeric" maxLength={10} name="phoneNumber" value={form.phoneNumber} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="Phone number" className={inputBase("phone")} required />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-2">Email</label>
                                    <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="you@example.com" className={inputBase("email")} required />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-2">Message</label>
                                    <textarea name="message" value={form.message} onChange={handleChange} onFocus={() => setFocused("message")} onBlur={() => setFocused(null)} placeholder="How can we help?" rows={4} className={`${inputBase("message")} resize-none`} required />
                                </div>
                                <button
                                    type="submit"
                                    disabled={emaildata?.isLoading}
                                    onClick={(e) => handleSubmit(e as any)}
                                    className="w-full bg-[var(--primary-color)] !text-white py-3 rounded-xl font-semibold cursor-pointer transition-colors disabled:opacity-50"
                                >
                                    {emaildata?.isLoading ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ============================================
// FOOTER
// ============================================

// Extract latest version from the markdown header table
const extractLatestVersion = (md: string): string => {
    const match = md.match(/\*\*Latest Version\*\*\s*\|\s*(v[\d.]+)/);
    return match ? match[1] : 'v1.0.0';
};

const Footer = () => {
    const isLoggedIn = Boolean(localStorage.getItem('AUTHDATA'));
    const [appVersion, setAppVersion] = useState('v1.1.0');

    useEffect(() => {
        import('../../RELEASE_NOTES.md?raw').then((mod) => {
            setAppVersion(extractLatestVersion(mod.default));
        });
    }, []);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <>
            <footer className="bg-slate-900 text-white pt-16 pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        <div className="col-span-2">
                            <div className="flex items-center gap-2 mb-5">
                                <span className="text-xl font-bold">DriveMynt</span>
                            </div>
                            <p className="text-slate-400 max-w-sm">
                                Empowering driving schools with modern technology to deliver better education and manage operations seamlessly.
                            </p>
                        </div>

                        {/* Product */}
                        <div>
                            <h4 className="font-semibold mb-4!">Product</h4>
                            <ul className="space-y-3 text-slate-400 text-sm">
                                <li><button onClick={() => scrollToSection('what-is-it')} className="hover:text-white transition-colors cursor-pointer">Features</button></li>
                                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
                                <li><button onClick={() => scrollToSection('mobile-app')} className="hover:text-white transition-colors cursor-pointer">Mobile App</button></li>
                                <li> <a href="/userGuide" className='hover:text-white transition-colors'>Docs</a></li>
                            </ul>
                        </div>

                        {/* Contact */}
                        <div>
                            <h4 className="font-semibold mb-4!">Contact</h4>
                            <ul className="space-y-3 text-slate-400 text-sm">
                                <li className="flex items-start gap-2">
                                    <GlobalOutlined className="w-4 h-4 mt-0.5" />
                                    <span>Chennai, India</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <PhoneIcon className="w-4 h-4" />
                                    <span>+91(44) 2278-1279</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <MailIcon className="w-4 h-4" />
                                    <span>contact@auxwit.com</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="py-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-slate-500 text-sm">
                        <p>© {new Date().getFullYear()} Auxwit Technologies Private Limited. All rights reserved.</p>
                        <div className="flex gap-6 items-center">
                            <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
                            <a href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a>
                            <span className="text-slate-600">|</span>
                            {isLoggedIn ? (
                                <a href="/driveMynt-Versions" className="hover:text-white transition-colors" style={{ color: 'var(--primary-color)', fontSize: '13px' }}>
                                    {appVersion}
                                </a>
                            ) : (
                                <span className="text-slate-500" style={{ fontSize: '13px' }}>{appVersion}</span>
                            )}
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
};

// ============================================
// FEATURES (kept for backward compatibility)
// ============================================
const Features = WhatIsIt;

// Default export: all below-fold sections — lazy-loaded by landing.tsx
const LandingBelowFold: React.FC = () => (
    <>
        <StatsStrip />
        <WhatIsIt />
        <HowItWorks />
        <MobileApp />
        <About />
        <SchoolReviews />
        <Contact />
        <Footer />
    </>
);

export default LandingBelowFold;
export { StatsStrip, WhatIsIt, HowItWorks, Testimonials, Features, MobileApp, About, SchoolReviews, Contact, Footer };
